/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Injectable } from '@angular/core';
import { AbstractCanvasConnection } from './AbstractCanvasConnection';
import { IPaperSize, paperSizes, PaperTypes, PaperUnits } from '../../../app-data/constants/paper-sizes';
import {
	defaultPrintConfiguration,
	IPrintConfiguration,
	IPrintStatus,
	PaperOrientations,
	PrintActions,
	PrintScopes,
	PrintTextSizes,
	RoleLegendOptions,
} from '../../../app-data/constants/plan-print.model';
import { Observable, Subject } from 'rxjs';
import { ICapturedTile } from '@appCanvasCommon/export/ExportCapture';

/**
 * PlanPrintService
 * Service responsible for managing plan print configuration state.
 * The service maintains the current print configuration and provides methods to update
 * individual settings. Changes are automatically dispatched to subscribers (like FancyPrint) via the inherited changed$ observable.
 */
@Injectable({ providedIn: 'root' })
export class PlanPrintService extends AbstractCanvasConnection<IPrintConfiguration | null> {
	private _printStatusUpdate$ = new Subject<IPrintStatus>();
	/**
	 * Observable stream of print status updates. Emits events as the print progresses.
	 * Useful for components that need to respond to print lifecycle events.
	 */
	public printStatusUpdate$: Observable<IPrintStatus> = this._printStatusUpdate$.asObservable();

	/**
	 * Current print configuration state. Null when print mode is not active.
	 */
	private _view!: IPrintConfiguration | null;

	private readonly _capturedRawTiles$ = new Subject<ICapturedTile[]>();

	/**
	 * Observable stream of the new raw captured tile arrays.
	 */
	public capturedRawTiles$ = this._capturedRawTiles$.asObservable();

	/**
	 * Emit a new array of the raw captured tiles.
	 */
	public emitCapturedRawTiles(tiles: ICapturedTile[]): void {
		this._capturedRawTiles$.next(tiles);
	}

	/**
	 * Emits a new print status update event. Subscribers use this to react to status updates.
	 * @param status - Print status object including configuration, current action, message, and an error flag
	 */
	public updatePrintStatus(status: IPrintStatus) {
		this._printStatusUpdate$.next(status);
	}

	/**
	 * Enter print mode with the specified configuration. This activates the print preview and notifies observers.
	 * Intention here is to keep the APi similar to `SplitData<blah>` objects, even if this isn't a SplitData.
	 * @param config - Print configuration to use, defaults to defaultPrintConfiguration
	 */
	public switchViewToEdit(config: IPrintConfiguration = defaultPrintConfiguration): void {
		this._view = config;
		this.dispatch();
		this.updatePrintStatus({
			config,
			status: PrintActions.Edit,
		});
	}

	/**
	 * Helper method to update the print status to Stitching
	 */
	public setStatusToStitch(): void {
		this.updatePrintStatus({
			config: this._view || undefined,
			status: PrintActions.CaptureStitch,
		});
	}
	/**
	 * Helper method to update the print status to Completed
	 */
	public setStatusToCompleted(): void {
		this.updatePrintStatus({
			config: this._view || undefined,
			status: PrintActions.PrintCompleted,
		});
	}
	/**
	 * Helper method to update the print status to Failed
	 */
	public setStatusToFailed(message: string): void {
		this.updatePrintStatus({
			config: this._view || undefined,
			status: PrintActions.PrintFailed,
			isError: true,
			message,
		});
	}

	/**
	 * Exit print mode and hide print preview. Sets configuration to null, which signals observers to disable print mode.
	 */
	public closeView(): void {
		this._view = null;
		this.dispatch();
		this.updatePrintStatus({
			config: this._view || undefined,
			status: PrintActions.PrintCompleted,
		});
	}

	/**
	 * Cancel the print process.
	 * This call does not exit from print mode. If the print process has started then calling this function can be used to cancel/stop it.
	 */
	public cancelPrintProcess(): void {
		this.update({ action: PrintActions.Cancel });
	}

	/**
	 * Replace the entire print configuration with a new one.
	 * @param config - Complete print configuration to apply
	 */
	public setConfig(config: IPrintConfiguration): void {
		this.update(config);
		this.updatePrintStatus({
			config,
			status: config.action,
		});
	}

	/**
	 * Update the page size to a standard paper type. Automatically updates width, height, and units based on the selected type.
	 * @param type - Standard paper type
	 */
	public setPageSizeType(type: PaperTypes): void {
		if (PaperTypes.Custom === type) {
			this.update({ pageSize: PaperTypes.Custom });
			return;
		}
		const size: IPaperSize | undefined = paperSizes.get(type);
		this.update({ pageSize: type, width: size?.width, height: size?.height });
	}

	/**
	 * Set custom page dimensions. Used when pageSize is set to 'custom' to allow arbitrary dimensions.
	 * @param width - Page width in current units
	 * @param height - Page height in current units
	 */
	public setPageSize(width: number, height: number): void {
		this.update({ width, height });
	}

	/**
	 * Change the measurement units for page dimensions. Note: This doesn't convert existing values, just changes the unit type.
	 * @param units - New unit type (inches, pixels, etc.)
	 */
	public setUnits(units: PaperUnits): void {
		this.update({ units });
	}

	/**
	 * Set page orientation (landscape or portrait). The tiling engine will apply this orientation when calculating final dimensions.
	 * @param orientation - Desired page orientation
	 */
	public setOrientation(orientation: PaperOrientations): void {
		this.update({ orientation });
	}

	/**
	 * Set the text size for rendered plan elements.
	 * @param textSize - Desired text size
	 */
	public setTextSize(textSize: PrintTextSizes): void {
		this.update({ textSize });
	}

	/**
	 * Toggle inclusion of role legend in the export. The role legend shows color coding for different ticket roles.
	 * @param roleLegendConfig - Whether to include the role legend
	 */
	public setRoleLegend(roleLegendConfig: RoleLegendOptions): void {
		this.update({ roleLegend: roleLegendConfig });
	}

	/**
	 * Toggle inclusion of project logo in the export. The logo will be positioned according to export component configuration.
	 * @param includeProjectLogo - Whether to include the project logo
	 */
	public shouldIncludeProjectLogo(includeProjectLogo: boolean): void {
		this.update({ includeProjectLogo });
	}

	/**
	 * Set the DPI (dots per inch) for export quality. Higher DPI produces better quality but larger files.
	 * @param dpi - Desired DPI
	 */
	public setDpi(dpi: number): void {
		this.update({ dpi });
	}

	/**
	 * Set print scope to date range and specify the date boundaries. This will automatically fit the lightbox to tickets within the specified dates.
	 * @param startDate - Start date
	 * @param endDate - End date
	 */
	public fitWithinDate(startDate: string, endDate: string): void {
		this.update({ scope: PrintScopes.FitWithinDate, startDate, endDate });
	}

	/**
	 * Set the print scope without additional parameters.
	 * @param scope - Desired print scope
	 */
	public setScope(scope: PrintScopes): void {
		this.update({ scope });
	}

	/**
	 * Helper method to start the process of capturing a single tile of the plan
	 */
	public captureSingle(): void {
		this.update({ action: PrintActions.CaptureSingle });
		this.updatePrintStatus({
			config: this._view || undefined,
			status: PrintActions.CaptureSingle,
		});
	}

	/**
	 * Helper method to start the process of capturing a multi-tile (and stitch) of the plan
	 */
	public captureMulti(): void {
		this.update({ action: PrintActions.CaptureStitch });
		this.updatePrintStatus({
			config: this._view || undefined,
			status: PrintActions.CaptureStitch,
		});
	}

	/**
	 * Private helper method to help update the local data state.
	 * @param data Partial<IPrintConfiguration> The data to update in the local state
	 * @private
	 */
	private update(data: Partial<IPrintConfiguration>): void {
		if (!this._view) {
			return;
		}
		this._view = { ...this._view, ...data };
		this.dispatch();
	}

	/**
	 * Helper method to dispatch current configuration state.
	 * @private
	 */
	private dispatch(): void {
		this._changed$.next(this._view);
	}
}
